import type { CampaignPoint, CustomerPace, PaceState } from "../api";
import { full, pct } from "../metrics/format";
import { money, signedPct } from "../revenue/format";

const STATE_LABEL: Record<PaceState, string> = {
  behind: "Behind",
  on_pace: "On pace",
  ahead: "Ahead",
  new: "New",
  stopped: "Stopped",
};

/**
 * Each campaign's dialling beside what it is earning, so a campaign that
 * dials plenty and bills little -- or has stopped and is costing the month
 * -- is one row rather than two tabs.
 *
 * Usage is the metrics rollup's 28 days; revenue is billing's month to
 * date. Both key on the platform's campaign id. A campaign present on one
 * side only still gets a row, with dashes on the other, because "billed
 * but not in the rollup" is itself worth seeing.
 */
export default function CampaignUsage({
  campaigns,
  pace,
}: {
  campaigns: CampaignPoint[];
  pace: CustomerPace | null | undefined;
}) {
  const billed = new Map((pace?.campaigns ?? []).map((c) => [c.campaign_id, c]));
  const ids = [...new Set([...campaigns.map((c) => c.campaign_id), ...billed.keys()])];
  if (ids.length === 0) return null;

  const usage = new Map(campaigns.map((c) => [c.campaign_id, c]));
  const total = pace ? Number(pace.mtd) : 0;
  const cur = pace?.currency ?? "INR";

  const rows = ids
    .map((id) => ({ id, u: usage.get(id), b: billed.get(id) }))
    .sort((x, y) => Number(y.b?.mtd ?? 0) - Number(x.b?.mtd ?? 0) || (y.u?.attempts ?? 0) - (x.u?.attempts ?? 0));

  return (
    <section className="ov-section" aria-labelledby="ov-camp">
      <div className="ov-head">
        <h2 id="ov-camp">Campaigns · usage and revenue</h2>
        <span className="dim">Usage: last 28 days{pace && " · Revenue: month to date vs same days last month"}</span>
      </div>
      <div className="ctable ov-ctable">
        <table>
          <thead>
            <tr>
              <th>Campaign</th>
              <th className="n">Leads</th>
              <th className="n">Connect</th>
              <th className="n">Att/lead</th>
              {pace && (
                <>
                  <th>Pace</th>
                  <th className="n">Revenue</th>
                  <th className="n">Share</th>
                  <th className="n">Same days</th>
                  <th className="n">Change</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {rows.map(({ id, u, b }) => {
              const change = b?.delta_pct ? Number(b.delta_pct) : null;
              return (
                <tr key={id}>
                  <td>
                    <span className="cid">{u?.identifier ?? b?.name ?? id}</span>
                  </td>
                  <td className="n">{u ? full(u.leads_created) : "—"}</td>
                  <td className="n">{pct(u?.connect_rate)}</td>
                  <td className="n">{u?.attempts_per_lead?.toFixed(2) ?? "—"}</td>
                  {pace && (
                    <>
                      <td>{b && <span className={`pill pace ${b.state}`}>{STATE_LABEL[b.state]}</span>}</td>
                      <td className="n">{b ? money(b.mtd, cur) : "—"}</td>
                      <td className="n">
                        {b && total > 0 ? `${((Number(b.mtd) / total) * 100).toFixed(0)}%` : "—"}
                      </td>
                      <td className="n">{b ? money(b.prior_mtd, cur) : "—"}</td>
                      <td className={change !== null && change < 0 ? "n bad" : "n"}>
                        {b ? signedPct(b.delta_pct) : "—"}
                      </td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
